import React, { useEffect, useState } from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import { helmetJsonLdProp } from "react-schemaorg";
import { Nav } from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown';
import Scrollspy from 'react-scrollspy';
import { Container, Row, Col } from "react-bootstrap/esm/index";

import ItpNavigation from "../../components/ITPNavigation/ITPNavigation";
import FooterITP from "../../components/shared-ui/Footer/Footer-ITP";
import BackToTopBtn from "../../components/shared-ui/BackToTopBtn/BackToTopBtn";
import ISIITPinline from "../../components/shared-ui/ISI/ISIITP-inline";
import ISIwrapper from "../../components/shared-ui/ISI/ISI-wrapper";
import ISIITP from "../../components/shared-ui/ISI/ISIITP";
import ITPHeroHeader from "../../components/HeroHeader/ITPHeroHeader";
import Accordion from "../../components/shared-ui/Accordion/Accordion";
import ITPAccordionSection from "../../components/shared-ui/Accordion/ITPAccordionSection";

// Import Styles
import "../../css/FAQ.scss";

function FAQ() {
  const [activeSection, setActiveSection] = useState('ABOUT ITP');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleVisibility = (event) => {
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    let location = window.location.pathname;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      page: {
        path: `${location}`,
        title: "DOPTELET® (avatrombopag) & Idiopathic Thrombocytopenic Purpura (ITP) FAQs",
        environment: "dev",
        indication: "ITP",
        audience: "DTC",
      },
    });
  }, []);
  return (
    <div className="main_content_wrap">
      <Helmet
        script={[
          helmetJsonLdProp({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Drug",
                name: "Doptelet",
                alternateName: "avatrombopag",
                manufacturer: {
                  "@type": "Organization",
                  name: "Sobi",
                },
                dosageForm: "Pill",
              },
              {
                "@type": "WebSite",
                about: {
                  "@type": "Thing",
                  description:
                    "To inform patients of Doptelet (avatrombopag), a prescription medicine used to treat low blood platelet counts in adults with chronic immune thrombocytopenia when other treatments have not worked well enough. Doptelet (avatrombopag) is also a prescription medicine used to treat low blood platelet counts in adults with long-lasting (chronic) liver disease who are scheduled to have a medical or dental procedure.",
                },
                url: "https://doptelethcp.com/itp/",
              },
              {
                "@type": "Brand",
                logo: "https://doptelet.com/images/doptelet-avatrombopag-tablets-logo.svg",
              },
              {
                "@type": "Brand",
                logo: "https://doptelet.com/images/sobi-logo.png",
              },
              {
                "@type": "MedicalAudience",
                audienceType:
                  "Patients with chronic immune thrombocytopenia when other treatments have not worked well enough. ",
                healthCondition: {
                  "@type": "MedicalCondition",
                  alternateName: "Immune Thrombocytopenia (ITP)",
                },
                requiredMinAge: "18",
              },
              {
                "@type": "MedicalTherapy",
                seriousAdverseOutcome: {
                  "@type": "MedicalEntity",
                  description:
                    "DOPTELET may cause serious side effects, including blood clots. People with chronic immune thrombocytopenia and people with certain blood clotting conditions may have an increased risk of developing blood clots. Tell your healthcare provider right away if you have signs and symptoms of a blood clot, including:Swelling, pain, or tenderness in your legs, Shortness of breath, Chest pain, Fast heartbeat, and Stomach (abdominal) pain or tenderness.",
                },
              },
              {
                "@type": "MedicalTherapy",
                adverseOutcome: {
                  "@type": "MedicalEntity",
                  description:
                    "'The most common side effects of DOPTELET when used to treat low blood platelet counts in adults with chronic immune thrombocytopenia (ITP) are: headache, tiredness, bruising, nosebleed, upper-respiratory tract infection, joint pain, gum bleeding, and purple or red spots on your skin.",
                },
              },
              {
                "@type": "HealthTopicContent",
                hasHealthAspect:
                  "The most common side effects of DOPTELET when used to treat low blood platelet counts in adults with chronic immune thrombocytopenia (ITP) are: headache, tiredness, bruising, nosebleed, upper-respiratory tract infection, joint pain, gum bleeding, purple or red spots on your skin, runny nose",
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "item": {
                      "@id": "https://doptelet.com/chronic-itp-faqs/",
                      "Name": "ITP"
                    }
                  }
                ]
              },
            ],
          }),
        ]}
      >
        <title>FAQs: Chronic ITP Patient Information | DOPTELET® (avatrombopag)
        </title>
        <meta
          name="description"
          content="Find answers to common questions about Chronic ITP and DOPTELET (avatrombopag), including information about treatment, dosing and patient support programs."
        />
        <link rel="canonical" href="https://doptelet.com/chronic-itp-faqs/" />
      </Helmet>
      <ItpNavigation />
      <div className="inner-content-wrap">
        <ITPHeroHeader
          extraClass="large-hero inner-bg"
          markup={
            <>
              <h1 className="mb-0">Frequently Asked Questions</h1>
            </>
          }
          maxWidth="515"
          bgImage={["faq-hero.jpg", "faq-hero@2x.jpg"]}
          bgImageMobile={[
            "faq-hero-mobile.jpg",
            "faq-hero-mobile@2x.jpg",
          ]}
          imageAlt="Doptelet character taking photo of standing face cutout in carnival scenery"
        />
        <div className="sticky-top mobile-sticky-list d-lg-none">
          <button className={dropdownOpen ? 'open' : ''} onClick={toggleVisibility}>{activeSection}</button>
          <Scrollspy
            className={dropdownOpen ? 'visible' : ''}
            items={['about-itp', 'managing-itp', 'itp-treatment', 'about-doptelet', 'dosing', 'financial-assistance-information']}
            currentClassName="is-active"
            onUpdate={ (element) => { if (element) { setActiveSection(element.textContent) }} }>
              <li><Nav.Link onClick={toggleVisibility} href="#about-itp">About ITP</Nav.Link></li>
              <li><Nav.Link onClick={toggleVisibility} href="#managing-itp">Managing ITP</Nav.Link></li>
              <li><Nav.Link onClick={toggleVisibility} href="#itp-treatment">ITP Treatment</Nav.Link></li>
              <li><Nav.Link onClick={toggleVisibility} href="#about-doptelet">About Doptelet</Nav.Link></li>
              <li><Nav.Link onClick={toggleVisibility} href="#dosing">Dosing</Nav.Link></li>
              <li><Nav.Link onClick={toggleVisibility} href="#financial-assistance-information">Financial Assistance Information</Nav.Link></li>
          </Scrollspy>
        </div>

        <section className="section-padding faqs">
          <Container>
            <Row>
              <Col md={12} lg={3}>
                <div className="d-none d-lg-block sticky-top sticky-list">
                  <p className="text-blue">On this page</p>
                  <Scrollspy
                    items={['about-itp', 'managing-itp', 'itp-treatment', 'about-doptelet', 'dosing', 'financial-assistance-information']}
                    currentClassName="is-active">
                      <li><Nav.Link href="#about-itp">ABOUT ITP</Nav.Link></li>
                      <li><Nav.Link href="#managing-itp">MANAGING ITP</Nav.Link></li>
                      <li><Nav.Link href="#itp-treatment">ITP TREATMENT</Nav.Link></li>
                      <li><Nav.Link href="#about-doptelet">ABOUT DOPTELET</Nav.Link></li>
                      <li><Nav.Link href="#dosing">DOSING</Nav.Link></li>
                      <li><Nav.Link href="#financial-assistance-information">FINANCIAL ASSISTANCE INFORMATION</Nav.Link></li>
                  </Scrollspy>
                </div>
              </Col>
              <Col md={12} lg={9} id="spy-section">
                <h2 id="about-itp" className="scroll-offset mb-3">About ITP</h2>
                <Accordion
                  title="What is ITP?"
                  markup={
                    <>
                      <p>Immune thrombocytopenia (ITP) is a blood disorder that causes low platelet counts.</p>
                    </>
                  }
                />
                <Accordion
                  title="What are platelets?"
                  markup={
                    <>
                      <p>Platelets are a type of blood cell that help keep blood vessel walls strong and intact. They also help your body stop bleeding by creating clots where needed.</p>
                    </>
                  }
                />
                <Accordion
                  title="What causes ITP?"
                  markup={
                    <>
                      <p>It’s not known what specifically causes ITP, but it stems from problems with the immune system. The immune system’s job is to protect against infection by making antibodies that attack, destroy, and remove foreign substances in the body. This includes bacteria, viruses, and toxins. With ITP, your immune system produces fewer platelets overall and mistakes your platelets for foreign substances and destroys them.</p>
                    </>
                  }
                />
                <Accordion
                  title="What are the symptoms of ITP?"
                  markup={
                    <>
                      <p>ITP is known to have a heterogeneous presentation, which means you may experience different signs and symptoms than someone else with ITP. Symptoms may include fatigue (feeling tired even after a good night’s rest), bruising easily, seeing petechiae and purpura spots on your skin, bleeding from your gums or nose, having an unusually heavy menstrual flow, or seeing blood in your urine, vomit, or stool. Read more about the <Link to="/what-is-itp/itp-signs-and-symptoms/">common signs and symptoms of ITP</Link>.</p>
                    </>
                  }
                />
                <Accordion
                  title="How is ITP diagnosed?"
                  markup={
                    <>
                      <p>ITP is sometimes referred to as a “diagnosis of exclusion.” But even though there’s no specific way to test for it, your doctor may run some of these common tests to determine if you have ITP.</p>
                      <ul>
                        <li><strong>Complete blood count (CBC)</strong><br />Your doctor will draw a sample of blood and use it to measure your number of blood cells, including your platelet counts</li>
                        <li><strong>CBC with differential</strong><br />A thin layer of your blood—normally taken as part of a CBC—is put on a glass slide and reviewed under a microscope to learn more about the number and shape of your blood cells</li>
                        <li><strong>Bone marrow examination</strong><br />In rare cases, if the other blood tests show that your platelet count is low, your doctor may want to collect a sample of your bone marrow (they’ll numb the area beforehand) to make sure it’s making enough platelets</li>
                      </ul>
                    </>
                  }
                />
                <Accordion
                  title="Is ITP hereditary?"
                  markup={
                    <>
                      <p>People with ITP don’t normally have a family history of thrombocytopenia (or low platelet counts), but if you do, you should let your doctor know so a possible genetic link can be considered.</p>
                    </>
                  }
                />
                <Accordion
                  title="Is ITP cancer?"
                  markup={
                    <>
                      <p>No, ITP is a blood disorder and an autoimmune condition in which your immune system attacks the platelets in your body.</p>
                    </>
                  }
                />
                <Accordion
                  title="Is ITP a type of hemophilia?"
                  markup={
                    <>
                      <p>While both hemophilia and ITP are bleeding disorders, ITP is not a form of hemophilia. Hemophilia is an inherited condition, while ITP is an autoimmune disorder. Both conditions are similar in that they affect the body’s ability to clot.</p>
                    </>
                  }
                />
                <Accordion
                  title="Is ITP an autoimmune condition?"
                  markup={
                    <>
                      <p>Yes, ITP is an autoimmune condition. When you have immune thrombocytopenia, your own immune system attacks the platelets in your body, leaving your platelet count low.</p>
                    </>
                  }
                />
                <Accordion
                  title="Who treats ITP?"
                  markup={
                    <>
                      <p>Typically, ITP is treated by hematologists, who specialize in treating blood disorders. ITP may also be treated by oncologists that specialize in hematology. These hematologist oncologists, or “Hem Oncs,” treat people with blood disorders like ITP and people with cancers associated with blood. ITP is not a cancer, so being referred to a Hem Onc does not mean your doctor thinks you have cancer.</p>
                    </>
                  }
                />
                <Accordion
                  title="Can ITP be cured?"
                  markup={
                    <>
                      <p>In adults, ITP is often a chronic disorder that is lifelong once diagnosed. With a care team and treatment plan, ITP can be managed to reduce signs and symptoms.</p>
                    </>
                  }
                />
                <Accordion
                  title="What is the difference between newly diagnosed, persistent, and chronic ITP?"
                  markup={
                    <>
                      <p>ITP is categorized based on the length of time with the condition. Newly diagnosed is from the time of diagnosis to 3 months, persistent is from 3-12 months, and chronic is having ITP for longer than a year. <Link to="/what-is-itp/#diagnosing">Read more about how different types of ITP are diagnosed</Link>.</p>
                    </>
                  }
                />
                <Accordion
                  title="Can ITP go away on its own?"
                  markup={
                    <>
                      <p>With newly diagnosed immune thrombocytopenia, there is a chance for it to go away within 3 months, but this type of ITP is found more commonly in children.</p>
                    </>
                  }
                />
                <Accordion
                  title="What is the difference between idiopathic thrombocytopenic purpura and immune thrombocytopenia?"
                  markup={
                    <>
                      <p>Immune thrombocytopenia and idiopathic thrombocytopenia purpura are the same disease. Immune thrombocytopenia is the newer term used to describe the blood disorder.</p>
                    </>
                  }
                />

                <h2 id="managing-itp" className="scroll-offset mb-3 mt-5">Managing ITP</h2>
                <Accordion
                  title="When should I be treated?"
                  markup={
                    <>
                      <p>A doctor will determine when/if treatment is necessary based on platelet counts and presenting symptoms. Talk to your doctor to understand how they are determining treatments.</p>
                    </>
                  }
                />
                <Accordion
                  title="What should my treatment goal be?"
                  markup={
                    <>
                      <p>While everyone’s treatment goal is unique (based on their age, medical history, activity level, etc), for most people, keeping a count of 50,000 platelets per microliter or more can help reduce risk. Talk to your doctor to figure out what your treatment goals should be.</p>
                    </>
                  }
                />
                <Accordion
                  title="How should I track my treatment goal?"
                  markup={
                    <>
                      <p>There are many ways to track goals, symptoms, and notes for future discussion. One helpful way is the my florio ITP™ app. It’s an all-in-one app designed to monitor all things ITP—platelet counts, bruising events, appointments, treatments, and more—plus, it’s free!</p>
                      <p>Get started with my florio ITP™ by downloading the app through the <a href="https://apps.apple.com/no/app/florio-itp/id1610121401" target="_blank">Apple Store</a> or <a href="https://play.google.com/store/apps/details?id=app.florio.itp.eu.mdd&pli=1" target="_blank">Google Play</a>.</p>
                    </>
                  }
                />
                <Accordion
                  title="How should I prepare for my doctor’s appointments?"
                  markup={
                    <>
                      <p>Tracking your symptoms is a great way to start preparing for doctor’s appointments. <Link to="/support-and-resources/living-with-itp/">You can sign up today to get a useful doctor’s appointment journal</Link>.</p>
                    </>
                  }
                />

                <h2 id="itp-treatment" className="scroll-offset mb-3 mt-5">ITP Treatment</h2>
                <Accordion
                  title="What types of treatment are available for ITP?"
                  markup={
                    <>
                      <p>There are a few different types of treatment for ITP. The most common are corticosteroids, immunosuppressants, thrombopoietin receptor agonists (TPO-RAs), and splenectomies. This is not a complete list of treatments. Talk to your doctor to understand all of your options and see what might work for you.</p>
                    </>
                  }
                />
                <Accordion
                  title="What is a splenectomy?"
                  markup={
                    <>
                      <p>A splenectomy is the removal of the spleen. The spleen is responsible for destroying platelets that are covered in antibodies. By taking out the spleen you can slow down platelet destruction. Some people may consider a splenectomy for ITP when other therapies fail.</p>
                    </>
                  }
                />
                <Accordion
                  title="What is an immunosuppressant?"
                  markup={
                    <>
                      <p>An immunosuppressant is a treatment that weakens the body’s immune system, which can be helpful when treating autoimmune conditions.</p>
                    </>
                  }
                />
                <Accordion
                  title="What is TPO?"
                  markup={
                    <>
                      <p>Thrombopoietin (TPO) is a hormone in the body that turns on the pathway that creates platelets.</p>
                    </>
                  }
                />
                <Accordion
                  title="Are there ITP treatment guidelines?"
                  markup={
                    <>
                      <p>Yes, there are 2 guidelines for diagnosing and treating ITP. They are the ‘American Society of Hematology 2019 Guidelines for immune thrombocytopenia’ and ‘Updated international consensus report on the investigation and management of primary immune thrombocytopenia.’</p>
                    </>
                  }
                />

                <h2 id="about-doptelet" className="scroll-offset mb-3 mt-5">About Doptelet</h2>
                <Accordion
                  title="How does Doptelet work?"
                  markup={
                    <>
                      <p>Doptelet mimics the way the hormone thrombopoietin (TPO) works in the body. Doptelet activates the same pathway as TPO, telling the body to make more platelets and increase platelet production.</p>
                    </>
                  }
                />
                <Accordion
                  title="How is Doptelet different from other treatments?"
                  markup={
                    <>
                      <p>Doptelet is the only oral thrombopoietin receptor agonist (TPO-RA) that helps lift your platelet count on your ideal schedule with no food-type restrictions, additional liver monitoring, or treatment injections.</p>
                      <p>Friendly reminder: Food’s a must when taking your dose of Doptelet, but which kind is up to you. When you start on Doptelet, your doctor will check your platelet count with a weekly blood test. Once you’ve reached your treatment goal, they’ll check it monthly.</p>
                      <p><Link to="/what-is-doptelet/">Discover why Doptelet can be the right ITP treatment option for you.</Link></p>
                    </>
                  }
                />
                <Accordion
                  title="Is Doptelet a steroid?"
                  markup={
                    <>
                      <p>No, Doptelet isn’t a steroid. It’s a thrombopoietin receptor agonist (TPO-RA) that can help lift your platelet count.</p>
                    </>
                  }
                />
                <Accordion
                  title="Was Doptelet studied in clinical trials?"
                  markup={
                    <>
                      <p>Yes. 49 patients living with chronic ITP were studied in a 6-month clinical trial for Doptelet. Some were taking Doptelet, some weren’t. On Day 8 of the trial, 21 out of the 32 patients taking Doptelet reached at least 50,000 platelets per microliter or more. Those not on Doptelet didn’t reach 50,000 platelets per microliter. Beyond that, the patients taking Doptelet were able to keep their platelet counts at 50,000 platelets per microliter or higher for an average of 12 total weeks vs 0 weeks for those not on Doptelet.</p>
                    </>
                  }
                />
                <Accordion
                  title="What are the side effects of Doptelet?"
                  markup={
                    <>
                      <p>The most common side effects of Doptelet are headache, tiredness, bruising, nosebleed, upper respiratory tract infection, joint pain, gum bleeding, purple or red spots on your skin (petechiae), and runny nose. Doptelet is a thrombopoietin receptor agonist (TPO-RA) and TPO-RAs have been associated with blood clots. Tell your doctor right away if you have symptoms of a blood clot, including: swelling, pain, or tenderness in your legs, shortness of breath, chest pain, fast heartbeat, and stomach (abdominal) pain or tenderness.</p>
                    </>
                  }
                />
                <Accordion
                  title="Will Doptelet make my platelet count normal?"
                  markup={
                    <>
                      <p>It’s important to know and remember that the goal of treatment may not be to normalize your platelet counts. Everyone’s platelet count goal is unique, so it’s up to you and your doctor to set and reach your goals together.</p>
                    </>
                  }
                />
                <Accordion
                  title="How often do I need platelet monitoring while taking Doptelet?"
                  markup={
                    <>
                      <p>After starting Doptelet, platelet counts will be monitored once a week until a stable platelet count greater than or equal to 50×10<sup>9</sup>/L has been achieved. Platelet counts will then be monitored once monthly or as necessary (as determined by your doctor).</p>
                    </>
                  }
                />
                <Accordion
                  title="Are there any food restrictions while taking Doptelet?"
                  markup={
                    <>
                      <p>With Doptelet, you have full food freedom, meaning you can eat any food you want, whenever you want. This includes calcium- and magnesium-rich foods. Food is required when taking Doptelet.</p>
                    </>
                  }
                />
                <Accordion
                  title="What do other patients say about Doptelet?"
                  markup={
                    <>
                      <p>Listen to real Doptelet patients talk about their lives after diagnosis, reaching treatment goals, and why Doptelet works for them <Link to="/patient-stories/">here</Link>.</p>
                    </>
                  }
                />

                <h2 id="dosing" className="scroll-offset mb-3 mt-5">Dosing</h2>
                <Accordion
                  title="What if I miss a dose of Doptelet?"
                  markup={
                    <>
                      <p>If you miss your dose of Doptelet, take it as soon as you remember. Don’t take 2 doses at once to make up for missing a dose. You should take your next dose at your usual time.</p>
                    </>
                  }
                />
                <Accordion
                  title="What if I take too much Doptelet?"
                  markup={
                    <>
                      <p>If you think you’ve taken more Doptelet than you were prescribed, call your doctor or go to the nearest emergency room right away.</p>
                    </>
                  }
                />
                <Accordion
                  title="How long will I be taking Doptelet?"
                  markup={
                    <>
                      <p>Everyone’s treatment goals are different, so it’s important to keep taking Doptelet exactly as your doctor prescribed, based on the treatment plan you agreed on together. If you have questions about how long you’ll be on Doptelet, <a href="/pdf/Dr-Discussion-Guide.pdf" target="_blank">talk to your doctor or care team</a>—they’re there to help.</p>
                    </>
                  }
                />

                <h2 id="financial-assistance-information" className="scroll-offset mb-3 mt-5">Financial Assistance Information</h2>
                <Accordion
                  title="How much does Doptelet cost?"
                  markup={
                    <>
                      <p>The cost of Doptelet may vary depending on your insurance.</p>
                    </>
                  }
                />
                <Accordion
                  title="Is there financial assistance available?"
                  className="mb-0"
                  markup={
                    <>
                      <p>Doptelet Connect has Dedicated Care Coordinators who can help research your individual coverage, coordinate with a pharmacy to fill your prescription and provide information on financial and patient assistance offerings that may be available to you. <Link to="/support-and-resources/financial-assistance/">Learn more</Link>.</p>
                    </>
                  }
                />
              </Col>
            </Row>
          </Container>
        </section>

        <ISIITPinline />
      </div>
      <FooterITP />
      <ISIwrapper>
        <ISIITP />
      </ISIwrapper>
      <BackToTopBtn />
    </div >
  );
}

export default FAQ;
